import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { X, Users, Crown, UserPlus, Search } from 'lucide-react';
import { teams } from '../lib/api';
import { toast } from 'sonner';

export default function TeamRegistrationModal({ tournament, user, onClose, onSuccess }) {
  const teamSize = tournament.team_size || 2;
  const slotsNeeded = teamSize - 1; // minus captain

  const [teamName, setTeamName] = useState('');
  const [members, setMembers] = useState(
    Array.from({ length: slotsNeeded }, () => ({
      pseudo: '', name: '', email: '', looking_for_teammate: false
    }))
  );
  const [loading, setLoading] = useState(false);

  const updateMember = (i, field, value) => {
    setMembers(prev => prev.map((m, idx) => idx === i ? { ...m, [field]: value } : m));
  };

  const toggleLooking = (i) => {
    setMembers(prev => prev.map((m, idx) =>
      idx === i ? { ...m, looking_for_teammate: !m.looking_for_teammate, pseudo: '', name: '', email: '' } : m
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!teamName.trim()) {
      toast.error("Nom d'équipe obligatoire");
      return;
    }
    // Validate: each non-looking member needs at least pseudo or (name+email)
    for (let i = 0; i < members.length; i++) {
      const m = members[i];
      if (!m.looking_for_teammate && !m.pseudo && !m.name) {
        toast.error(`Joueur ${i + 2}: pseudo ou nom requis`);
        return;
      }
    }

    setLoading(true);
    try {
      await teams.create(tournament.id, {
        team_name: teamName,
        captain_id: user.id,
        members: members.map(m => ({
          pseudo: m.pseudo || null,
          name: m.name || null,
          email: m.email || null,
          user_id: null,
          looking_for_teammate: m.looking_for_teammate,
        })),
      });
      toast.success('Équipe enregistrée !');
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[#1a1a1a] border border-[#333] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="team-registration-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[#333]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FF6B35]/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-[#FF6B35]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Inscription par équipe</h2>
              <p className="text-[#666] text-xs">Équipe de {teamSize} joueurs</p>
            </div>
          </div>
          <button onClick={onClose} className="text-[#666] hover:text-white" data-testid="close-team-modal"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {/* Team name */}
          <div>
            <Label className="text-[#B0B0B0]">Nom d'équipe *</Label>
            <Input value={teamName} onChange={e => setTeamName(e.target.value)}
              placeholder="Ex: Les Planeswalkers" className="bg-[#252525] border-[#333] text-white mt-1"
              data-testid="team-name-input" required />
          </div>

          {/* Captain (current user) */}
          <div className="p-3 rounded-xl bg-[#FF6B35]/5 border border-[#FF6B35]/20">
            <div className="flex items-center gap-2 mb-1">
              <Crown className="w-4 h-4 text-[#FF6B35]" />
              <span className="text-sm font-bold text-[#FF6B35]">Capitaine</span>
            </div>
            <p className="text-white text-sm">{user.pseudo || user.name || user.email}</p>
            <p className="text-[#666] text-xs">Vous êtes automatiquement le capitaine de l'équipe</p>
          </div>

          {/* Other members */}
          {members.map((m, i) => (
            <div key={i} className="p-4 rounded-xl bg-[#0a0a0a] border border-[#333] space-y-3" data-testid={`team-member-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-white">Joueur {i + 2}</span>
                <button type="button" onClick={() => toggleLooking(i)}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    m.looking_for_teammate
                      ? 'bg-[#4ECDC4]/15 text-[#4ECDC4] border border-[#4ECDC4]/30'
                      : 'bg-[#252525] text-[#666] hover:text-white'
                  }`}
                  data-testid={`looking-toggle-${i}`}>
                  <Search className="w-3 h-3" />
                  {m.looking_for_teammate ? 'Cherche coéquipier' : 'Trouve-moi un équipier'}
                </button>
              </div>

              {m.looking_for_teammate ? (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-[#4ECDC4]/5 border border-[#4ECDC4]/20">
                  <UserPlus className="w-4 h-4 text-[#4ECDC4] shrink-0" />
                  <p className="text-[#4ECDC4] text-xs">Ce slot sera visible sur la fiche tournoi. Les joueurs pourront vous contacter pour rejoindre votre équipe.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <div>
                    <Label className="text-[#888] text-xs">Pseudo du joueur</Label>
                    <Input value={m.pseudo} onChange={e => updateMember(i, 'pseudo', e.target.value)}
                      placeholder="Pseudo Arcanapp" className="bg-[#1a1a1a] border-[#333] text-white text-sm" />
                  </div>
                  <p className="text-[#555] text-xs text-center">— ou à défaut —</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[#888] text-xs">Nom</Label>
                      <Input value={m.name} onChange={e => updateMember(i, 'name', e.target.value)}
                        placeholder="Nom complet" className="bg-[#1a1a1a] border-[#333] text-white text-sm" />
                    </div>
                    <div>
                      <Label className="text-[#888] text-xs">Email</Label>
                      <Input value={m.email} onChange={e => updateMember(i, 'email', e.target.value)}
                        placeholder="email@..." className="bg-[#1a1a1a] border-[#333] text-white text-sm" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Info box */}
          <div className="p-3 rounded-lg bg-[#252525] border border-[#333]">
            <p className="text-[#888] text-xs leading-relaxed">
              {members.some(m => m.looking_for_teammate)
                ? "Votre équipe sera visible sur la fiche du tournoi comme incomplète. Les joueurs pourront vous contacter par message pour rejoindre votre équipe. Aucun paiement tant que l'équipe n'est pas complète."
                : tournament.price > 0
                  ? `Le paiement de ${tournament.price}€ par équipe sera demandé une fois l'équipe complète.`
                  : "L'équipe sera inscrite directement une fois complète."
              }
            </p>
          </div>

          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 border-[#333] text-white">Annuler</Button>
            <Button type="submit" disabled={loading} className="flex-1 bg-[#FF6B35] hover:bg-[#E55A25] text-white" data-testid="submit-team-btn">
              <Users className="w-4 h-4 mr-2" />
              {loading ? 'Inscription...' : "Inscrire l'équipe"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
